import React, { useState, useEffect, useCallback } from 'react';
import { Button, Select, TableSkeleton, Modal } from '../components/common';
import statementService from '../services/statement.service';
import { Invoice, InvoiceLine, InvoiceFilters } from '../types';
import './PurchaseHistory.css';

type PeriodoTipo = 'mes_actual' | 'mes_anterior';

const calcularFechas = (tipo: PeriodoTipo) => {
  const now = new Date();
  let startDate = '';
  let endDate = '';

  switch (tipo) {
    case 'mes_actual':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
    case 'mes_anterior':
      const primerDiaMesAnterior = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const ultimoDiaMesAnterior = new Date(now.getFullYear(), now.getMonth(), 0);
      startDate = primerDiaMesAnterior.toISOString().split('T')[0];
      endDate = ultimoDiaMesAnterior.toISOString().split('T')[0];
      break;
  }

  return { startDate, endDate };
};

const PurchaseHistory: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [periodoTipo, setPeriodoTipo] = useState<PeriodoTipo>('mes_actual');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });
  const [filters, setFilters] = useState<InvoiceFilters>(() => {
    const { startDate, endDate } = calcularFechas('mes_actual');
    return { startDate, endDate, status: '' };
  });

  // Modal de detalle
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceLines, setInvoiceLines] = useState<InvoiceLine[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const loadInvoices = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await statementService.getInvoices({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });
      setInvoices(data.invoices);
      setPagination(prev => ({
        ...prev,
        ...data.pagination
      }));
    } catch (error) {
      console.error('Error cargando facturas:', error);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handlePeriodoChange = (tipo: PeriodoTipo) => {
    setPeriodoTipo(tipo);
    const { startDate, endDate } = calcularFechas(tipo);
    setFilters(prev => ({ ...prev, startDate, endDate }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setPeriodoTipo('mes_actual');
    const { startDate, endDate } = calcularFechas('mes_actual');
    setFilters({ startDate, endDate, status: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewDetail = async (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsModalOpen(true);
    setIsLoadingDetail(true);

    try {
      const data = await statementService.getInvoiceDetail(invoice.id);
      setInvoiceLines(data.lines);
    } catch (error) {
      console.error('Error cargando detalle:', error);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      paid: { label: 'Pagada', className: 'badge-success' },
      not_paid: { label: 'Pendiente', className: 'badge-error' },
      partial: { label: 'Parcial', className: 'badge-warning' },
      reversed: { label: 'Pagada con NC', className: 'badge-reversed' }
    };
    const { label, className } = statusMap[status] || { label: status, className: '' };
    return <span className={`badge ${className}`}>{label}</span>;
  };

  // Badge para tipo de documento (Factura vs Nota de Crédito)
  const getDocumentTypeBadge = (moveType: string) => {
    if (moveType === 'out_refund') {
      return <span className="badge badge-refund">Nota de Crédito</span>;
    }
    return <span className="badge badge-invoice">Factura</span>;
  };

  return (
    <div className="purchase-history">
      {/* Filters */}
      <div className="filters-card">
        <div className="filters-header">
          <h3>Filtros de Búsqueda</h3>
          <p>Selecciona el periodo y estado para filtrar tu historial de facturas</p>
        </div>
        <div className="filters-grid">
          <div className="period-selector">
            <label className="period-label">Periodo</label>
            <div className="period-buttons">
              <button
                className={`period-btn ${periodoTipo === 'mes_actual' ? 'active' : ''}`}
                onClick={() => handlePeriodoChange('mes_actual')}
              >
                Mes actual
              </button>
              <button
                className={`period-btn ${periodoTipo === 'mes_anterior' ? 'active' : ''}`}
                onClick={() => handlePeriodoChange('mes_anterior')}
              >
                Mes anterior
              </button>
            </div>
          </div>
          <Select
            label="Estado"
            value={filters.status || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            options={[
              { value: '', label: 'Todos' },
              { value: 'paid', label: 'Pagadas' },
              { value: 'not_paid', label: 'Pendientes' },
              { value: 'partial', label: 'Parciales' }
            ]}
          />
          <div className="filters-actions">
            <Button onClick={handleSearch} variant="primary">
              Buscar
            </Button>
            <Button onClick={handleClearFilters} variant="ghost">
              Limpiar
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-card">
        {isLoading ? (
          <TableSkeleton rows={5} cols={6} />
        ) : (
          <>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Tipo</th>
                    <th>Fecha</th>
                    <th>Vencimiento</th>
                    <th>Total</th>
                    <th>Pendiente</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length > 0 ? (
                    invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="cell-primary">{invoice.name}</td>
                        <td>{getDocumentTypeBadge(invoice.move_type)}</td>
                        <td>{formatDate(invoice.invoice_date)}</td>
                        <td>{formatDate(invoice.invoice_date_due)}</td>
                        <td className="cell-amount">{formatCurrency(invoice.amount_total)}</td>
                        <td className="cell-amount cell-pending">
                          {formatCurrency(invoice.amount_residual)}
                        </td>
                        <td>{getStatusBadge(invoice.payment_state)}</td>
                        <td>
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() => handleViewDetail(invoice)}
                          >
                            Ver detalle
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="empty-cell">
                        <div className="empty-state">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          <p>No se encontraron facturas</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <span className="pagination-info">
                  Mostrando {invoices.length} de {pagination.total} registros
                </span>
                <div className="pagination-controls">
                  <Button
                    variant="ghost"
                    size="small"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Anterior
                  </Button>
                  <span className="pagination-page">
                    Página {pagination.page} de {pagination.totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="small"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`Detalle de ${selectedInvoice?.name || 'Factura'}`}
        size="large"
      >
        {isLoadingDetail ? (
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>Cargando detalle...</p>
          </div>
        ) : (
          <div className="invoice-detail">
            <div className="detail-header">
              <div className="detail-info">
                <p><strong>Fecha:</strong> {selectedInvoice && formatDate(selectedInvoice.invoice_date)}</p>
                <p><strong>Vencimiento:</strong> {selectedInvoice && formatDate(selectedInvoice.invoice_date_due)}</p>
              </div>
              <div className="detail-totals">
                <p><strong>Subtotal:</strong> {selectedInvoice && formatCurrency(selectedInvoice.amount_untaxed)}</p>
                <p><strong>IVA:</strong> {selectedInvoice && formatCurrency(selectedInvoice.amount_tax)}</p>
                <p className="total"><strong>Total:</strong> {selectedInvoice && formatCurrency(selectedInvoice.amount_total)}</p>
              </div>
            </div>

            <h4>Productos/Servicios</h4>
            <div className="detail-table-wrapper">
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th>Cantidad</th>
                    <th>Precio Unit.</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceLines.map((line) => (
                    <tr key={line.id}>
                      <td>{line.name}</td>
                      <td>{line.quantity}</td>
                      <td>{formatCurrency(line.price_unit)}</td>
                      <td>{formatCurrency(line.price_subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PurchaseHistory;
